import React, { useEffect } from 'react';
import { Form } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { clearFieldErrors, handleFormErrors } from '../../common/errorHelpers';
import { BreadcrumbItem, Breadcrumbs } from '../../components/Breadcrumbs';
import { DetailsPageHeader } from '../../components/DetailsPageHeader';
import { PageRoutesEnum } from '../../utils/routes';

import { ClientDataForm } from './ClientDataForm';
import { ClientEmployeesTable } from './ClientEmployeesTable';
import { ClientProjectsTable } from './ClientProjectsTable';
import { ClientRemindersTable } from './ClientRemindersTable';
import { useGetClient } from './useGetClient';
import { useUpdateClient } from './useUpdateClient';

const MainContainer = styled.div`
  display: flex;
  height: 100vh;
  flex-direction: column;
  padding: 20px;
`;

const RelatedTables = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
  margin-top: 10px;

  > * {
    min-width: 0;
  }

  @media (max-width: 992px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const EditClientPage: React.FC = () => {
  const { id } = useParams();
  const { data, isLoading } = useGetClient(id || '');
  const [form] = Form.useForm();
  const updateClient = useUpdateClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    form.setFieldsValue({
      ...data,
    });
  }, [data, form, id]);

  if (isLoading || !data) return <></>;

  const handleSave = () => {
    const dataValues = form.getFieldsValue();
    updateClient.mutate(
      { queryParams: { id }, data: dataValues },
      {
        onSuccess: () => clearFieldErrors(form),
        onError: (error) => {
          handleFormErrors({ error, form });
        },
      }
    );
  };

  const handleCancel = () => navigate(-1);

  const breadcrumbItems = [
    {
      title: <BreadcrumbItem path={PageRoutesEnum.clients} title="Clients" />,
    },
    {
      title: <BreadcrumbItem title={`${data.name}`} />,
    },
  ];

  return (
    <MainContainer>
      <DetailsPageHeader
        title={<Breadcrumbs breadcrumbItems={breadcrumbItems} />}
        onSave={handleSave}
        onCancel={handleCancel}
      />
      <ClientDataForm form={form} />
      <RelatedTables data-testid="client-related-tables">
        <ClientProjectsTable projects={data.projects} />
        <ClientEmployeesTable clientId={id ?? ''} />
      </RelatedTables>
      <ClientRemindersTable clientId={id ?? ''} />
    </MainContainer>
  );
};
